import React, { useCallback, useEffect, useState } from 'react';
import { BackLink } from 'ui/BackLink';
import classnames from 'classnames';
import { ETypeValueType } from 'services/CrmApi/types/types';
import { VerticalSpace } from 'ui/VerticalSpace';
import { useRouteMatch } from 'react-router';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { useResponsive } from '../hooks/useResponsive';
import { useCrmCountries } from 'services/CrmApi/queries/useCrmCountries';
import { useCrmTypeValues } from 'services/CrmApi/queries/useCrmTypeValues';
import { useCrmDirectoryEntry } from 'services/CrmApi/queries/useCrmDirectoryEntry';
import { ECrmDirectoryEntryType, ICrmContactProfile, ICrmPerson } from 'services/CrmApi/types/DirectoryEntry';
import { CrmContactTabs } from './CrmContactTabs/CrmContactTabs';
import { ContactPageTitle } from '../components/ContactPageTitle/ContactPageTitle';
import { CrmContactEditProfile } from './CrmContactProfile/CrmContactEditProfile';
import { CrmPublishDirectoryEntry } from '../components/CrmPublishDirectoryEntry/CrmPublishDirectoryEntry';
import { isContactProfile } from '../helpers';
import { useSelector } from 'react-redux';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { SubscriptionBellButton } from '../components/SubscriptionBellButton/SubscriptionBellButton';
import { IUser } from 'services/BackendApi';

interface ICrmContactEditRouteParams {
  contactId: string;
}

export const CrmContactEdit = () => {
  const match = useRouteMatch<ICrmContactEditRouteParams>();
  const contactId = match.params.contactId;
  const { isMobile, isTablet, isDesktop, isDesktopHD } = useResponsive();

  const isSales = useSelector(AuthSelectors.isSales);
  const isFinance = useSelector(AuthSelectors.isFinanceUser);
  const isAdmin = useSelector(AuthSelectors.isAdmin);
  const currentUser = useSelector(AuthSelectors.getCurrentUser) as IUser;
  
  const crmCountries = useCrmCountries();
  const crmTypeValuesContacts = useCrmTypeValues(ETypeValueType.CONTACT, { enabled: true });
  const { crmDirectoryEntryResponse, directoryEntry } = useCrmDirectoryEntry(contactId);
  
  const contacts = [... (directoryEntry?.linkedDirectoryEntries || []) ].filter( directoryEntry => directoryEntry.type === ECrmDirectoryEntryType.PERSON );
  const companies = [... (directoryEntry?.linkedDirectoryEntries || []) ].filter( directoryEntry => directoryEntry.type === ECrmDirectoryEntryType.COMPANY );

  // Directory entry profile type
  const directoryEntryProfile = directoryEntry?.profile as ICrmContactProfile;
  const directoryEntryProfileType = directoryEntryProfile?.contactType.code ?? '';
  const [selectedDirectoryEntryType, setSelectedDirectoryEntryType] = useState(directoryEntryProfileType);
  useEffect(() => {
    setSelectedDirectoryEntryType(directoryEntryProfileType);
  }, [directoryEntryProfileType]);
  const handleChangeDirectoryEntryProfileType = useCallback((profileType: string | undefined) => {
    setSelectedDirectoryEntryType(profileType ?? '');
  }, []);
  
  const messages: string[] = [];
  if (directoryEntry?.profile.type === ECrmDirectoryEntryType.PERSON
    && isContactProfile(directoryEntry?.profile)
    && !directoryEntry?.profile.lastName) {
    messages.push('Last name is required.');
  }
  if (directoryEntry?.profile.type === ECrmDirectoryEntryType.PERSON
    && isContactProfile(directoryEntry?.profile)
    && !directoryEntry?.profile.titleType) {
      messages.push('Title is required.');
  }
  if (!directoryEntry?.email) {
    messages.push('Email is required.');
  }
  if (!directoryEntry?.country) {
    messages.push('Country is required.');
  }
  const relatedTCs = companies.filter(item => item.companyType?.code === 'TC');
  if (relatedTCs?.length > 1) {
    messages.push('Related only to one Travel Company.');
  }
  if (relatedTCs?.length === 0) {
    messages.push('Related to a Travel Company.');
  }
  if (relatedTCs?.length === 1 && !relatedTCs[0].publishedDate) {
    messages.push('Related Travel Company needs to be published.');
  }
  const isPublished = !!directoryEntry?.publishedDate;
  const isTravelAgent = directoryEntry?.profile.type === ECrmDirectoryEntryType.PERSON
    && isContactProfile(directoryEntry?.profile)
    && directoryEntry?.profile.contactType.code === 'TA'; // TA is the code for Travel Agent

  // Subscribers
  const isSubscribed = !!directoryEntry?.subscribers?.some(subscriber => subscriber.id === currentUser?.uuid);

  const layoutClassname = classnames('flex gap-[20px]', {
    'flex-col': isMobile || isTablet,
    'flex-row': isDesktop || isDesktopHD,
  });
  
  const onLinkOrUnlink = useCallback( () => {
    crmDirectoryEntryResponse.refetch();
  }, [crmDirectoryEntryResponse]);

  if (crmCountries.isPending || crmTypeValuesContacts.isPending || crmDirectoryEntryResponse.isPending) {
    return (
      <div className="mt-[20px]">
        <LoadingBar />
      </div>
    )
  }

  if (crmCountries.isError || crmTypeValuesContacts.isError || !directoryEntry || !directoryEntry.profile) {
    return (
      <div className="mt-[20px]">
        <ErrorBar />
      </div>
    )
  }
 
  if (directoryEntry.profile.type !== ECrmDirectoryEntryType.PERSON || !('contactType' in directoryEntry.profile)) {
    return (
      <div className="mt-[20px]">
        <ErrorBar message="It seems this is not a contact profile" />
      </div>
    )
  }
  
  return (
    <div className="crm-directory mx-[20px] crm-desktop:mx-[80px]">
      <div className="flex items-center justify-between">
        <BackLink type="internal" href="/crm/directory">
          Back
        </BackLink>
        <SubscriptionBellButton directoryEntry={directoryEntry} currentUser={currentUser} isSubscribed={isSubscribed} />
      </div>
      <ContactPageTitle
        directoryEntry={directoryEntry}
        contactType={directoryEntry?.profile.contactType.value}
      />
      <VerticalSpace height="20px" />
      <div className={layoutClassname}>
        <CrmContactEditProfile isPublished={isPublished} directoryEntry={directoryEntry} onChangeDirectoryEntryProfileType={handleChangeDirectoryEntryProfileType} />
        <div className="flex-1 flex flex-col gap-[40px] min-w-0">
          {!isPublished && (isAdmin || isFinance || isSales) && isTravelAgent && (
            <CrmPublishDirectoryEntry
              profileType={ECrmDirectoryEntryType.PERSON}
              messages={messages}
              directoryEntryId={directoryEntry.id}
            />
          )}
          <CrmContactTabs 
            directoryEntry={directoryEntry} 
            directoryEntryProfileType={selectedDirectoryEntryType}
            contacts={contacts} 
            companies={companies} 
            onLinkOrUnlink={onLinkOrUnlink} 
          />
        </div>
      </div>
    </div>
  )
};
